import { DEFAULT_USER_DETAILS } from '@common/constants'
import { isPlaceholderPhone } from '@lib/utils/identifier'
import { enc } from 'crypto-js'
import SHA256 from 'crypto-js/sha256'

// Outschool signups hold a placeholder address on this domain, like ours on
// `DEFAULT_USER_DETAILS.emailDomain`. Mirrors `DUMMY_OUTSCHOOL_DOMAIN`.
const OUTSCHOOL_DUMMY_EMAIL_DOMAIN = '@outschool-student.com'

type MetaUser = {
  id: number
  email?: string | null
  phone?: string | null
  city?: string | null
}

/**
 * Advanced-matching user data for the Meta Pixel's `fbq('init')`.
 *
 * Mirrors `build_user_data` in `api/apps/meta/utils.py` key for key and digest
 * for digest: the pixel and the Conversions API describe the same person, and a
 * key the two derive differently is one Meta silently cannot match on. The
 * tests pin the backend's digests.
 *
 * Name, gender and date of birth are not sent. The account email is the
 * parent's while those describe the student, so pairing them tells Meta the two
 * are one person - the server leaves them out for the same reason.
 */
export const buildMetaUserData = (
  user: MetaUser,
  countryCode?: string | null,
  cookies: Record<string, string> = {}
): Record<string, string> => {
  const email = (user.email ?? '').trim().toLowerCase()
  const isPlaceholderEmail =
    email.endsWith(DEFAULT_USER_DETAILS.emailDomain) ||
    email.endsWith(OUTSCHOOL_DUMMY_EMAIL_DOMAIN)

  // The email-signup placeholder is a number nobody answers, so it would only
  // add a key matching nobody.
  const phone = isPlaceholderPhone(user.phone)
    ? ''
    : (user.phone ?? '').replace(/\D/g, '').replace(/^0+/, '')

  const country = (countryCode ?? '').toLowerCase().replace(/[^a-z]/g, '')

  const normalized = {
    em: isPlaceholderEmail ? '' : email,
    ph: phone,
    // Anything but an ISO alpha-2 code hashes to a value matching nobody.
    country: country.length === 2 ? country : '',
    // Meta's ct rule keeps letters only, so spaces, digits and punctuation go.
    // Built at runtime because the Babel bundled with Next 11 cannot compile a
    // \p{...} regex literal and fails the build.
    ct: (user.city ?? '')
      .toLowerCase()
      .replace(new RegExp('[^\\p{L}]', 'gu'), ''),
    external_id: String(user.id),
  }

  const userData: Record<string, string> = {}
  for (const [key, value] of Object.entries(normalized)) {
    // crypto-js hashes only strings - a number is read as its own byte-array
    // type and every value comes out as one fixed digest - so each value here
    // must be a string. Empty ones are omitted, since hash('') matches nobody.
    if (value) userData[key] = SHA256(value).toString(enc.Hex)
  }

  // The pixel's own cookies go in plain text; hashing them breaks matching.
  if (cookies._fbc) userData.fbc = cookies._fbc
  if (cookies._fbp) userData.fbp = cookies._fbp

  return userData
}
